<template>
	<div
		ref="ref__dropdown"
		class="container__dropdown"
		:class="{ 'opacity-60': disabled }"
	>
		<div class="container__dropdown__label">
			<div class="flex gap-1 items-center">
				<label class="text-label">{{ label }}</label>
				<slot name="tooltip" />
			</div>
		</div>
		<div class="container__dropdown__element">
			<div
				class="container__dropdown__component"
				:class="[{ disabled }, help.type]"
			>
				<label
					class="container__dropdown__component__default flex justify-between py-0.5 px-1"
					@click="switchDropdown"
				>
					<p
						v-if="typeof value === 'string'"
						class="label__current_label text-base"
					>
						{{ options?.find((o) => o.value === value)?.label || placeholder }}
					</p>
					<div
						v-else-if="value instanceof Array"
						class="flex w-full"
					>
						<div
							v-if="escolhas.length && !showDropdown"
							class="p-2 flex items-center justify-between w-full"
						>
							<Tag
								rounded-full
								closeable
								@close="setValue(options.find((f) => f.label === escolhas[0]).value)"
							>
								<template #icon>
									<Icon
										icon="uil:times"
										height="18"
									/>
								</template>
								{{ escolhas[0] }}
							</Tag>
							<span
								v-if="escolhas.length > 1"
								class="font-bold text-base"
								>+{{ escolhas.length - 1 }} itens</span
							>
						</div>
						<div
							v-else-if="escolhas.length && showDropdown"
							class="p-2 flex-wrap gap-1 flex items-center justify-between w-full"
						>
							<Tag
								v-for="(item, index) in escolhas"
								:key="index"
								class="flex items-center"
								:label="item"
								@click="setValue(options.find((f) => f.label === item).value)"
							>
								<template #icon>
									<Icon
										icon="uil:times"
										height="18"
									/>
								</template>
							</Tag>
						</div>
						<p
							v-else
							class="label__current_label"
						>
							{{ $t('selecioneOuPesquise') }}
						</p>
					</div>
					<Icon
						icon="material-symbols:keyboard-arrow-down-rounded"
						height="24"
						color="#737576"
						:class="{ active: showDropdown }"
					/>
				</label>
				<ul
					v-if="showDropdown"
					ref="ref__dropdown__list"
					:class="['container__dropdown__component__list', 'downward']"
				>
					<li class="select-all-container">
						<span
							class="select-all-text"
							@click="selectAll"
						>
							{{ $t('selectAll') }}
						</span>
						<span
							class="select-all-text"
							@click="removeAll"
						>
							{{ $t('removeAll') }}
						</span>
					</li>
					<li class="search-wrapper">
						<input
							v-model="searchQuery"
							type="text"
							placeholder="Pesquisar"
							class="search-input"
						/>
						<Icon
							icon="material-symbols:search-rounded"
							height="20"
							color="#8BBE44"
							class="search-icon"
						/>
					</li>
					<li
						v-for="(option, index) in filteredOptions"
						:id="'option' + option.value"
						:key="index"
						class="font-normal !text-base container__dropdown__component__list__item"
						@click="setValue(option.value)"
					>
						<span class="flex gap-3">
							<Checkbox
								v-if="isMultiple"
								v-model="value"
								:model-value="option.value"
							/>
							{{ option.label }}
						</span>
						<span class="!text-xs">{{ option.example }}</span>
					</li>
				</ul>
			</div>
			<slot />
		</div>
		<span
			v-show="Object.entries(help).length > 0"
			class="message__help__dropdown"
			>{{ help.message }}</span
		>
	</div>
</template>

<script setup>
import { Icon } from '@iconify/vue2'
import { onClickOutside } from '@vueuse/core'
import { computed, ref, toRefs, watch } from 'vue'
import Checkbox from '../checkbox/Checkbox.vue'
import Tag from '../tag/Tag.vue'

const props = defineProps({
	help: {
		type: Object,
		default: () => ({ message: '', type: '' }),
		required: false,
	},
	label: { type: String, default: '' },
	placeholder: { type: String, default: '' },
	options: Array,
	value: { type: [String, Array, Boolean] },
	multiple: { type: Boolean, default: false },
	disabled: { type: Boolean, default: false },
})

const { options, value, disabled } = toRefs(props)
const showDropdown = ref(false)
const searchQuery = ref('')
const selectAll = () => {
	const allValues = options.value.map((option) => option.value)
	emit('input', allValues)
	emit('change', allValues)
}
const removeAll = () => {
	emit('input', [])
	emit('change', [])
}
const escolhas = computed(() =>
	value.value?.length
		? options.value.filter((m) => value.value.includes(m.value)).map((m) => m.label)
		: [],
)
const filteredOptions = computed(() =>
	options.value.filter((option) =>
		option.label.toLowerCase().includes(searchQuery.value.toLowerCase()),
	),
)
const emit = defineEmits(['onInput', 'input', 'change'])
const isMultiple = computed(() => value.value instanceof Array)
const switchDropdown = () => {
	if (!disabled.value) {
		showDropdown.value = !showDropdown.value
	}
}

const setValue = (selectedValue) => {
	if (!isMultiple.value) {
		emit('input', selectedValue)
		emit('change', selectedValue)
	} else {
		const newValue = value.value.includes(selectedValue)
			? value.value.filter((f) => f !== selectedValue)
			: [...value.value, selectedValue]
		emit('input', newValue)
		emit('change', newValue)
	}
	if (!isMultiple.value) showDropdown.value = false
}

watch(value, (newValue) => {
	emit('input', newValue)
})

const ref__dropdown__list = ref(null)
onClickOutside(ref__dropdown__list, () => {
	showDropdown.value = false
})
</script>

<style scoped>
p {
	margin: 0 !important;
}
.search-wrapper {
	display: flex;
	align-items: center;
	padding: 4px;
	border-bottom: 1px solid #ccc;
}

.search-input {
	flex: 1;
	padding: 8px;
	border: none;
	outline: none;
}

.search-icon {
	margin-left: 8px;
}
.container__dropdown__component__list {
	max-height: 200px; /* Ajuste conforme necessário */
	overflow-y: auto;
	border: 1px solid #ccc;
	border-radius: 4px;

	position: absolute;
	background: white;
	z-index: 10;
	width: 100%;
}

.container__dropdown__component__list__item {
	padding: 8px;
	cursor: pointer;
}

.container__dropdown__component__list__item:hover {
	background-color: #f0f0f0;
}

.container__dropdown__component__list__item .checkbox-wrapper {
	transform: scale(1.5);
	margin-right: 8px;
}

.select-all-container {
	display: flex;
	justify-content: space-between;
	padding: 8px 12px;
	cursor: pointer;
	font-size: 14px;
	font-weight: bold;
	color: #004085;
	border-bottom: 1px solid #e0e0e0;
}

.select-all-text {
	color: #004085;
	text-decoration: none;
	cursor: pointer;
}

.select-all-text:hover {
	text-decoration: underline;
}
</style>
